import React, { useState, useEffect, useRef } from "react";
import { useDebounce } from "../../../../core/common/useDebounce";
import API_URLS from "../../../../core/common/apiUrls";

interface LocationDropdownProps {
    label: string;
    defaultCity: string;
    defaultAirport: string;
    isSwappable?: boolean;
    onChange?: (selectedLocation: any) => void;
    onSwap?: () => void;
}

interface LocationResult {
    airport_name: string;
    city_name: string;
    country_name: string;
    iata_code: string;
}

export const LocationDropdown: React.FC<LocationDropdownProps> = ({
    label,
    defaultCity,
    defaultAirport,
    isSwappable,
    onChange,
    onSwap,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [results, setResults] = useState<LocationResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Close on outside click
    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    useEffect(() => {
        if (debouncedSearchTerm.trim() === "") {
            setResults([]);
            return;
        }

        setLoading(true);
        fetch(API_URLS.FLIGHTS.SEARCH_LOCATION + encodeURIComponent(debouncedSearchTerm))
            .then((res) => res.json())
            .then((data) => {
                setResults(data.results || []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [debouncedSearchTerm]);

    const handleSelectLocation = (location: LocationResult) => {
        if (onChange) {
            onChange({
                name: location.city_name,
                country: location.country_name,
                iataCode: location.iata_code,
                airportName: location.airport_name,
            });
        }
        setSearchTerm(location.city_name);
        setResults([]);
        setIsOpen(false);
    };

    const showDropdown = isOpen && (loading || results.length > 0 || searchTerm.trim() !== "");

    return (
        <div className="form-item" style={{ position: "relative" }} ref={containerRef}>
            <label className="form-label fs-14 text-default mb-1">{label}</label>
            <input
                type="text"
                className="form-control"
                value={searchTerm}
                placeholder={defaultCity}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                autoComplete="off"
            />
            <p className="fs-12 mb-0">{defaultAirport}</p>
            {isSwappable && (
                <span
                    className="way-icon badge badge-primary rounded-pill translate-middle"
                    onClick={onSwap}
                    style={{ cursor: "pointer" }}
                >
                    <i className="fa-solid fa-arrow-right-arrow-left" />
                </span>
            )}

            {showDropdown && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        background: "#fff",
                        border: "1px solid #dee2e6",
                        borderRadius: "0.375rem",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        maxHeight: 280,
                        overflowY: "auto",
                    }}
                >
                    {loading && (
                        <div className="px-3 py-2 text-muted small">
                            <em>Searching...</em>
                        </div>
                    )}
                    {!loading && results.length === 0 && searchTerm.trim() !== "" && (
                        <div className="px-3 py-2 text-muted small">
                            <em>No results found</em>
                        </div>
                    )}
                    {results.map((loc, index) => (
                        <div
                            key={index}
                            className="px-3 py-2 border-bottom"
                            style={{ cursor: "pointer" }}
                            onMouseDown={(e) => {
                                e.preventDefault(); // prevent blur before click fires
                                handleSelectLocation(loc);
                            }}
                        >
                            <div className="fw-medium fs-14">
                                {loc.city_name} ({loc.iata_code})
                            </div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                                {loc.airport_name}, {loc.country_name}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
